/*
    Copyright © DokiApp, 2026
*/

import React from 'react'
import {
  Box,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@material-ui/core'
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowForward as ArrowForwardIcon,
} from '@material-ui/icons'

const EVALUATION_COLORS = {
  'NAGYON ALACSONY': '#BB285C',
  ALACSONY: '#FF5D55',
  ÁTLAGOS: '#F4EB49',
  MAGAS: '#AEDD94',
}

const EVALUATION_TEXT_COLORS = {
  'NAGYON ALACSONY': '#fff',
  ALACSONY: '#fff',
  ÁTLAGOS: '#1E2864',
  MAGAS: '#1E2864',
}

// Short labels for the 4 evaluation bands, indexed by ascending score.
// The backend returns `evaluations` ordered by scoreLowerLimit.
const EVALUATION_BAND_LABELS = [
  'NAGYON ALACSONY',
  'ALACSONY',
  'ÁTLAGOS',
  'MAGAS',
]

const useStyles = makeStyles((theme) => ({
  arrowForward: {
    fontSize: '3rem',
    color: '#454D7F',
  },
  arrowUpward: {
    fontSize: '4rem',
    color: '#454D7F',
  },
  desktopView: {
    [theme.breakpoints.up('xs')]: {
      display: 'flex',
      visibility: 'visible',
    },
    [theme.breakpoints.down('xs')]: {
      display: 'none',
      visibility: 'hidden',
    },
  },
  evaluationList: {
    margin: '0 0',
  },
  evaluationListItem: {
    textAlign: 'center',
    fontSize: '1rem',
  },
  evaluationTable: {
    margin: '0 2rem',
  },
  mobileView: {
    [theme.breakpoints.up('xs')]: {
      display: 'none',
      visibility: 'hidden',
    },
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
      visibility: 'visible',
      marginBottom: '1.5rem',
    },
  },
  selected: {
    fontWeight: '800',
  },
  tableCell: {
    padding: 0,
    border: 'none',
  },
}))

const EvaluationBandBar = ({ evaluations, evaluation }) => {
  const classes = useStyles()
  const currentLower = evaluation?.scoreLowerLimit

  const evaluationBands = (evaluations || []).map((band, index) => {
    const label = EVALUATION_BAND_LABELS[index]
    return {
      label,
      bgColor: EVALUATION_COLORS[label],
      textColor: EVALUATION_TEXT_COLORS[label],
      isSelected: band.scoreLowerLimit === currentLower,
    }
  })

  return (
    <>
      {/* Desktop evaluation bar */}
      <Grid container className={[classes.evaluationList, classes.desktopView]}>
        {evaluationBands.map((band) => (
          <Grid
            item
            xs={12}
            sm={3}
            key={band.label}
            className={classes.evaluationListItem}
          >
            <Box
              sx={{
                bgcolor: band.bgColor,
                p: 2,
                color: band.textColor,
              }}
              className={band.isSelected ? classes.selected : ''}
            >
              {band.label}
            </Box>
          </Grid>
        ))}
      </Grid>
      <Grid container className={[classes.evaluationList, classes.desktopView]}>
        {evaluationBands.map((band) => (
          <Grid
            item
            xs={12}
            sm={3}
            key={`arrow-${band.label}`}
            className={classes.evaluationListItem}
          >
            <Box>
              {band.isSelected && (
                <ArrowUpwardIcon className={classes.arrowUpward} />
              )}
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Mobile evaluation bar */}
      <Grid container className={classes.mobileView}>
        <Table className={classes.evaluationTable} padding="0">
          <TableBody>
            {evaluationBands.map((band) => (
              <TableRow key={band.label}>
                <TableCell className={classes.tableCell}>
                  <Box>
                    {band.isSelected && (
                      <ArrowForwardIcon className={classes.arrowForward} />
                    )}
                  </Box>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Box
                    sx={{
                      bgcolor: band.bgColor,
                      p: 2,
                      color: band.textColor,
                    }}
                    className={band.isSelected ? classes.selected : ''}
                  >
                    {band.label}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Grid>
    </>
  )
}

EvaluationBandBar.displayName = 'EvaluationBandBar'

export default EvaluationBandBar
export { EvaluationBandBar }
